import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiSearch,
  FiFilter,
  FiDownload,
  FiEye,
  FiEyeOff,
  FiUser,
  FiFileText,
  FiDollarSign,
  FiCalendar, 
  FiClock, 
  FiCheckCircle, 
  FiXCircle, 
  FiPhone,
  FiX,
  FiBriefcase,
  FiTarget
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { applicationsAPI, categoriesAPI, usersAPI, banksAPI, statesAPI } from '../services/api';
import { API_CONFIG } from '../config/api';
import DataTable from 'react-data-table-component';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { saveAs } from 'file-saver';
import ImageUpload from '../components/ImageUpload';
import { useAuth } from '../contexts/AuthContext';

// Constants
const PAGE_SIZE = 10;
const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'under_review', label: 'Under Review', color: 'bg-blue-100 text-blue-800' },
  { value: 'approved', label: 'Approved', color: 'bg-green-100 text-green-800' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
  { value: 'disbursed', label: 'Disbursed', color: 'bg-purple-100 text-purple-800' }
];

const Applications = () => {
  // State management
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filterText, setFilterText] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingApplication, setEditingApplication] = useState(null);
  const [viewingApplication, setViewingApplication] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Data state
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [states, setStates] = useState([]);
  
  // User creation state
  const [showUserCreate, setShowUserCreate] = useState(false);
  const [newUserData, setNewUserData] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    password: '' 
  });

  // Effects
  useEffect(() => {
    fetchApplications();
  }, [currentPage, pageSize, searchTerm, statusFilter]);

  useEffect(() => {
    fetchCategories();
    fetchBanks();
    fetchStates();
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUsers();
    } else if (user?.role === 'agent') {
      fetchAgentUsersSafe();
    }
  }, [user]);

  // API Functions
  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await applicationsAPI.getApplications(
        currentPage, 
        pageSize, 
        searchTerm, 
        statusFilter
      );
      
      const data = response?.data || [];
      const pagination = response?.pagination || {};
      
      setApplications(Array.isArray(data) ? data : []);
      setTotalPages(pagination.totalPages || 1);
      setTotalItems(pagination.totalItems || 0);
      
    } catch (error) {
      console.error('Error fetching applications:', error);
      
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        // The AuthContext will handle the redirect
        return;
      }
      
      toast.error('Failed to load applications');
      setApplications([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await banksAPI.getBanks();
      setBanks(response.data || response || []);
    } catch (error) {
      console.error('Error fetching banks:', error);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await statesAPI.getStates();
      setStates(response.data || response || []);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAgentUsersSafe = async () => {
    try {
      const response = await usersAPI.getMyUsers(); // Use the new agent-specific endpoint
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching agent users:', error);
      
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        // The AuthContext will handle the redirect
        return;
      }
      
      // Handle server errors gracefully
      if (error.response?.status === 500) {
        console.error('Server error details:', error.response?.data);
        toast.error('Server error loading users. Please try again later.');
        setUsers([]);
        return;
      }
      
      // Handle other errors
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        toast.error('Could not fetch users. Please try again or contact admin.');
      } else {
        toast.error('Error fetching users');
      }
      setUsers([]);
    }
  };

  // Event Handlers
  const handleOpenModal = () => {
    setShowModal(true);
    setEditingApplication(null);
    setViewingApplication(null);
    if (user?.role === 'agent') {
      fetchAgentUsersSafe();
    }
  };

  const handleView = (application) => {
    setViewingApplication(application);
    setShowModal(true);
  };

  const handleEdit = (application) => {
    setEditingApplication(application);
    setShowModal(true);
  };

  const handleDelete = async (applicationId) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        await applicationsAPI.deleteApplication(applicationId);
        toast.success('Application deleted successfully');
        fetchApplications();
      } catch (error) {
        console.error('Error deleting application:', error);
        toast.error('Failed to delete application');
      }
    }
  };

  const handleSave = async (applicationData) => {
    try {
      if (editingApplication) {
        const statusChanged = editingApplication.status !== applicationData.status;
        
        if (statusChanged && user?.role === 'admin') {
          const comment = applicationData.statusComment || `Status updated to ${applicationData.status} by admin`;
          await applicationsAPI.updateApplicationStatus(editingApplication._id, applicationData.status, comment);
        }
        
        const { status, statusComment, ...otherFields } = applicationData;
        await applicationsAPI.updateApplication(editingApplication._id, otherFields);
        
        toast.success('Application updated successfully');
      } else {
        await applicationsAPI.createApplication(applicationData);
        toast.success('Application created successfully');
      }
      
      setShowModal(false);
      setEditingApplication(null);
      setViewingApplication(null);
      fetchApplications();
    } catch (error) {
      console.error('Error saving application:', error);
      
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        // The AuthContext will handle the redirect
        return;
      }
      
      if (error.response?.data?.errors) {
        error.response.data.errors.forEach(err => {
          toast.error(`${err.path}: ${err.msg}`, { duration: 4000 });
        });
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to save application');
      }
    }
  };

  // Export Functions
  const exportToExcel = () => {
    const exportData = applications.map(app => ({
      'Application ID': app._id,
      'User': app.user?.name || 'N/A',
      'Category': app.category?.name || 'N/A',
      'Loan Amount': app.loanAmount,
      'Loan Purpose': app.loanPurpose,
      'Status': app.status,
      'Created Date': new Date(app.createdAt).toLocaleDateString(),
      'Preferred Bank': app.preferredBank || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Applications');
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, 'applications.xlsx');
    toast.success('Applications exported to Excel successfully');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('Applications Report', 14, 22);
    doc.setFontSize(12);
    
    const tableData = applications.map(app => [
      app._id.substring(0, 8),
      app.user?.name || 'N/A',
      app.category?.name || 'N/A',
      `₹${app.loanAmount}`,
      app.status,
      new Date(app.createdAt).toLocaleDateString()
    ]);
    
    doc.autoTable({
      head: [['ID', 'User', 'Category', 'Amount', 'Status', 'Date']],
      body: tableData,
      startY: 30,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [66, 139, 202] }
    });
    
    doc.save('applications.pdf');
    toast.success('Applications exported to PDF successfully');
  };

  // Utility Functions
  const getStatusColor = (status) => {
    const statusOption = STATUS_OPTIONS.find(option => option.value === status);
    return statusOption ? statusOption.color : 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <FiClock className="w-3 h-3" />;
      case 'under_review': return <FiFileText className="w-3 h-3" />;
      case 'approved': return <FiCheckCircle className="w-3 h-3" />;
      case 'rejected': return <FiXCircle className="w-3 h-3" />;
      case 'disbursed': return <FiDollarSign className="w-3 h-3" />;
      default: return <FiClock className="w-3 h-3" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Table Configuration
  const columns = [
    {
      name: 'Application ID',
      selector: row => row._id.substring(0, 8),
      sortable: true,
      width: '120px'
    },
    {
      name: 'User',
      selector: row => row.user?.name || 'N/A',
      sortable: true,
      width: '150px'
    },
    {
      name: 'Category',
      selector: row => row.category?.name || 'N/A',
      sortable: true,
      width: '120px'
    },
    {
      name: 'Loan Amount',
      selector: row => row.loanAmount,
      sortable: true,
      width: '120px',
      cell: row => <span className="font-medium">{formatCurrency(row.loanAmount)}</span>
    },
    {
      name: 'Loan Purpose',
      selector: row => row.loanPurpose,
      sortable: true,
      width: '200px',
      cell: row => (
        <div className="max-w-xs truncate" title={row.loanPurpose}>
          {row.loanPurpose}
        </div>
      )
    },
    {
      name: 'Preferred Bank',
      selector: row => row.preferredBank || 'N/A',
      sortable: true,
      width: '150px'
    },
    {
      name: 'Status',
      selector: row => row.status,
      sortable: true,
      width: '120px',
      cell: row => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(row.status)}`}>
          {getStatusIcon(row.status)}
          <span className="ml-1">{row.status}</span>
        </span>
      )
    },
    {
      name: 'Created Date',
      selector: row => row.createdAt,
      sortable: true,
      width: '120px',
      cell: row => <span className="text-sm text-gray-600">{formatDate(row.createdAt)}</span>
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleView(row)}
            className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
            title="View"
          >
            <FiEye className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleEdit(row)}
            className="p-1 text-green-600 hover:text-green-800 transition-colors"
            title="Edit"
          >
            <FiEdit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(row._id)}
            className="p-1 text-red-600 hover:text-red-800 transition-colors"
            title="Delete"
          >
            <FiTrash2 className="w-4 h-4" />
          </button>
        </div>
      ),
      width: '120px'
    }
  ];

  // Filtered data
  const filteredApplications = applications.filter(app => {
    const matchesSearch = !filterText || 
      app.user?.name?.toLowerCase().includes(filterText.toLowerCase()) ||
      app.category?.name?.toLowerCase().includes(filterText.toLowerCase()) ||
      app.loanPurpose?.toLowerCase().includes(filterText.toLowerCase()) ||
      app.preferredBank?.toLowerCase().includes(filterText.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || app.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || app.category?._id === filterCategory;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Loan Applications</h1>
          <button
            onClick={handleOpenModal}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <FiPlus className="w-4 h-4" />
            <span>Add Application</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder="Search applications..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                {STATUS_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Categories</option>
                {categories.map(category => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end space-x-2">
              <button
                onClick={exportToExcel}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
              >
                <FiDownload className="w-4 h-4" />
                <span>Excel</span>
              </button>
              <button
                onClick={exportToPDF}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
              >
                <FiDownload className="w-4 h-4" />
                <span>PDF</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        <DataTable
          title=""
          columns={columns}
          data={filteredApplications}
          pagination
          paginationServer
          paginationTotalRows={totalItems}
          onChangePage={setCurrentPage}
          onChangeRowsPerPage={setPageSize}
          progressPending={loading}
          responsive
          highlightOnHover
          pointerOnHover
          noDataComponent={
            <div className="text-center py-8">
              <FiFileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No applications found</p>
            </div>
          }
        />
      </div>

      {/* Application Modal */}
      <AnimatePresence>
        {showModal && (
          <ApplicationModal
            application={editingApplication || viewingApplication}
            viewingApplication={viewingApplication}
            editingApplication={editingApplication}
            categories={categories}
            users={users}
            banks={banks}
            states={states}
            user={user}
            onSave={handleSave}
            onClose={() => {
              setShowModal(false);
              setEditingApplication(null);
              setViewingApplication(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Application Modal Component
const ApplicationModal = ({ application, viewingApplication, editingApplication, categories, users, banks, states, user, onSave, onClose }) => {
  const isViewMode = viewingApplication && !editingApplication;
  const isAddMode = !application && !viewingApplication && !editingApplication;
  const currentApplication = viewingApplication || editingApplication || application;
  
  // Status helper functions
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'under_review': return 'bg-blue-100 text-blue-800';
      case 'disbursed': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <FiCheckCircle className="w-4 h-4 text-green-500" />;
      case 'pending': return <FiClock className="w-4 h-4 text-yellow-500" />;
      case 'rejected': return <FiXCircle className="w-4 h-4 text-red-500" />;
      case 'under_review': return <FiEye className="w-4 h-4 text-blue-500" />;
      case 'disbursed': return <FiCheckCircle className="w-4 h-4 text-purple-500" />;
      default: return <FiClock className="w-4 h-4 text-gray-500" />;
    }
  };
  
  const [formData, setFormData] = useState({
    // User details
    user: currentApplication?.user?._id || '',
    // Loan details
    category: currentApplication?.category?._id || '',
    loanType: currentApplication?.loanType || 'personal',
    loanAmount: currentApplication?.loanAmount || '',
    loanPurpose: currentApplication?.loanPurpose || '',
    monthlyIncome: currentApplication?.monthlyIncome || '',
    // Employment details
    employmentType: currentApplication?.employmentType || 'salaried',
    companyName: currentApplication?.companyName || '',
    workExperience: currentApplication?.workExperience || '',
    // Address details
    address: {
      street: currentApplication?.address?.street || '',
      city: currentApplication?.address?.city || '',
      state: currentApplication?.address?.state || '',
      pincode: currentApplication?.address?.pincode || ''
    },
    // Documents
    documents: {
      aadharCard: currentApplication?.documents?.aadharCard || '',
      panCard: currentApplication?.documents?.panCard || '',
      bankStatement: currentApplication?.documents?.bankStatement || '',
      salarySlip: currentApplication?.documents?.salarySlip || '',
      otherDocuments: currentApplication?.documents?.otherDocuments || []
    },
    // Aadhar and PAN numbers
    aadharNumber: currentApplication?.aadharNumber || '',
    panNumber: currentApplication?.panNumber || '',
    // Preferred Bank
    preferredBank: currentApplication?.preferredBank || '',
    // Status - Set to pending for agents, allow admin to change
    status: currentApplication?.status || (user?.role === 'agent' ? 'pending' : 'pending'),
    remarks: currentApplication?.remarks || ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});
    
    // Frontend validation
    const validationErrors = {};
    
    // Validate loan amount
    if (!formData.loanAmount || formData.loanAmount < 1000) {
      validationErrors.loanAmount = 'Loan amount must be at least ₹1,000';
    }
    
    // Validate monthly income
    if (!formData.monthlyIncome || formData.monthlyIncome <= 0) {
      validationErrors.monthlyIncome = 'Monthly income must be greater than 0';
    }
    
    // If there are validation errors, show them and stop submission
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }
    
    try {
      await onSave(formData);
    } catch (error) {
      // Handle validation errors
      if (error.response && error.response.data && error.response.data.errors) {
        const validationErrors = error.response.data.errors;
        const fieldErrors = {};
        
        validationErrors.forEach(err => {
          fieldErrors[err.path] = err.msg;
        });
        
        setErrors(fieldErrors);
      } else {
        // Re-throw the error to be handled by parent
        throw error;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFieldError = (fieldName) => {
    return errors[fieldName];
  };

  const hasFieldError = (fieldName) => {
    return !!errors[fieldName];
  };

  const clearFieldError = (fieldName) => {
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    clearFieldError(fieldName);
    
    // Auto-generate loanType from category
    if (fieldName === 'category' && value) {
      const selectedCategory = categories.find(cat => cat._id === value);
      if (selectedCategory) {
        const loanType = selectedCategory.name.toLowerCase().replace(/\s+/g, '-');
        setFormData(prev => ({ ...prev, loanType }));
      }
    }
  };

  const handleAddressChange = (fieldName, value) => {
    setFormData(prev => ({ 
      ...prev, 
      address: { ...prev.address, [fieldName]: value } 
    }));
    clearFieldError(`address.${fieldName}`);
  };

  const handleDocumentChange = (fieldName, value) => {
    setFormData(prev => ({ 
      ...prev, 
      documents: { ...prev.documents, [fieldName]: value } 
    }));
    clearFieldError(`documents.${fieldName}`);
  };

  // Helper function to get the full file URL
  const getFileUrl = (filePath) => {
    if (!filePath) return '';
    
    // If it's already a full URL, return as is
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    
    // If it's a data URL, return as is
    if (filePath.startsWith('data:')) {
      return filePath;
    }
    
    // Construct the full URL using the API base URL
    // Remove '/api' from the base URL since files are served from the root
    const baseUrl = API_CONFIG.BASE_URL.replace('/api', '');
    return `${baseUrl}/${filePath}`;
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={handleClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">
              {isViewMode ? 'View Application Details' : isAddMode ? 'Add New Application' : 'Edit Application'}
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <FiX className="w-6 h-6" />
            </button>
          </div>
          
          {isViewMode ? (
            // View Mode
            <div className="space-y-6">
              {/* Applicant Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Applicant Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Application Number</label>
                    <p className="text-gray-900 font-semibold">{currentApplication.applicationNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <p className="text-gray-900">{currentApplication.user?.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="text-gray-900">{currentApplication.user?.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <p className="text-gray-900">{currentApplication.user?.phone}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(currentApplication.status)}`}>
                      {currentApplication.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Loan Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Loan Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Category</label>
                    <p className="text-gray-900">{currentApplication.category?.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Loan Type</label>
                    <p className="text-gray-900">{currentApplication.loanType}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Loan Amount</label>
                    <p className="text-gray-900">₹{currentApplication.loanAmount?.toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Monthly Income</label>
                    <p className="text-gray-900">₹{currentApplication.monthlyIncome?.toLocaleString()}</p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Loan Purpose</label>
                    <p className="text-gray-900">{currentApplication.loanPurpose}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Preferred Bank</label>
                    <p className="text-gray-900">{currentApplication.preferredBank || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              {/* Employment Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Employment Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Employment Type</label>
                    <p className="text-gray-900">{currentApplication.employmentType}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company Name</label>
                    <p className="text-gray-900">{currentApplication.companyName}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Work Experience</label>
                    <p className="text-gray-900">{currentApplication.workExperience} years</p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Address</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">Street</label>
                    <p className="text-gray-900">{currentApplication.address?.street}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <p className="text-gray-900">{currentApplication.address?.city}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">State</label>
                    <p className="text-gray-900">{currentApplication.address?.state}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Pincode</label>
                    <p className="text-gray-900">{currentApplication.address?.pincode}</p>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Documents</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Aadhar Card</label>
                    {currentApplication.documents?.aadharCard ? (
                      <a href={getFileUrl(currentApplication.documents.aadharCard)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                        View Aadhar Card
                      </a>
                    ) : (
                      <p className="text-gray-500">Not uploaded</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">PAN Card</label>
                    {currentApplication.documents?.panCard ? (
                      <a href={getFileUrl(currentApplication.documents.panCard)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                        View PAN Card
                      </a>
                    ) : (
                      <p className="text-gray-500">Not uploaded</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Bank Statement</label>
                    {currentApplication.documents?.bankStatement ? (
                      <a href={getFileUrl(currentApplication.documents.bankStatement)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                        View Bank Statement
                      </a>
                    ) : (
                      <p className="text-gray-500">Not uploaded</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Salary Slip</label>
                    {currentApplication.documents?.salarySlip ? (
                      <a href={getFileUrl(currentApplication.documents.salarySlip)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                        View Salary Slip
                      </a>
                    ) : (
                      <p className="text-gray-500">Not uploaded</p>
                    )}
                  </div>
                  {currentApplication.documents?.otherDocuments?.length > 0 && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Other Documents</label>
                      <div className="space-y-1">
                        {currentApplication.documents.otherDocuments.map((doc, index) => (
                          <a key={index} href={getFileUrl(doc)} target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:text-blue-800">
                            Document {index + 1}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Remarks */}
              {currentApplication.remarks && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Remarks</h4>
                  <p className="text-gray-900">{currentApplication.remarks}</p>
                </div>
              )}

              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            // Edit/Create Mode
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Applicant Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Applicant Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Applicant *</label>
                    <select
                      value={formData.user}
                      onChange={(e) => handleFieldChange('user', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('user') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    >
                      <option value="">Select Applicant</option>
                      {users.map(user => (
                        <option key={user._id} value={user._id}>
                          {user.name} - {user.email}
                        </option>
                      ))}
                    </select>
                    {hasFieldError('user') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('user')}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Loan Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Loan Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => handleFieldChange('category', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('category') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    >
                      <option value="">Select Category</option>
                      {categories.map(category => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    {hasFieldError('category') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('category')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Loan Type (Auto-generated)</label>
                    <input
                      type="text"
                      value={formData.loanType || ''}
                      onChange={(e) => handleFieldChange('loanType', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('loanType') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Will be auto-generated from category"
                      readOnly
                    />
                    <p className="text-xs text-gray-500 mt-1">Loan type is automatically generated from the selected category</p>
                    {hasFieldError('loanType') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('loanType')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Loan Amount * (Min: ₹1,000)</label>
                    <input
                      type="number"
                      value={formData.loanAmount}
                      onChange={(e) => handleFieldChange('loanAmount', e.target.value)}
                      min="1000"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('loanAmount') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter loan amount (minimum ₹1,000)"
                      required
                    />
                    {hasFieldError('loanAmount') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('loanAmount')}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Minimum loan amount is ₹1,000</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Income *</label>
                    <input
                      type="number"
                      value={formData.monthlyIncome}
                      onChange={(e) => handleFieldChange('monthlyIncome', e.target.value)}
                      min="1"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('monthlyIncome') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter monthly income"
                      required
                    />
                    {hasFieldError('monthlyIncome') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('monthlyIncome')}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Enter your monthly income in ₹</p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Loan Purpose *</label>
                    <textarea
                      value={formData.loanPurpose}
                      onChange={(e) => handleFieldChange('loanPurpose', e.target.value)}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('loanPurpose') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    />
                    {hasFieldError('loanPurpose') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('loanPurpose')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Bank *</label>
                    <select
                      value={formData.preferredBank}
                      onChange={(e) => handleFieldChange('preferredBank', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('preferredBank') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    >
                      <option value="">Select Preferred Bank</option>
                      {banks.map(bank => (
                        <option key={bank._id} value={bank.name}>
                          {bank.name}
                        </option>
                      ))}
                    </select>
                    {hasFieldError('preferredBank') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('preferredBank')}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Employment Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Employment Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type *</label>
                    <select
                      value={formData.employmentType}
                      onChange={(e) => handleFieldChange('employmentType', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('employmentType') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    >
                      <option value="salaried">Salaried</option>
                      <option value="self-employed">Self Employed</option>
                      <option value="business">Business</option>
                      <option value="student">Student</option>
                      <option value="other">Other</option>
                    </select>
                    {hasFieldError('employmentType') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('employmentType')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => handleFieldChange('companyName', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('companyName') ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {hasFieldError('companyName') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('companyName')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Work Experience (Years)</label>
                    <input
                      type="number"
                      value={formData.workExperience}
                      onChange={(e) => handleFieldChange('workExperience', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('workExperience') ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {hasFieldError('workExperience') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('workExperience')}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Address</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Street *</label>
                    <input
                      type="text"
                      value={formData.address.street}
                      onChange={(e) => handleAddressChange('street', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('address.street') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    />
                    {hasFieldError('address.street') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('address.street')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                    <input
                      type="text"
                      value={formData.address.city}
                      onChange={(e) => handleAddressChange('city', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('address.city') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    />
                    {hasFieldError('address.city') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('address.city')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                    <select
                      value={formData.address.state}
                      onChange={(e) => handleAddressChange('state', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('address.state') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    >
                      <option value="">Select State</option>
                      {states.map(state => (
                        <option key={state._id} value={state.name}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                    {hasFieldError('address.state') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('address.state')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pincode *</label>
                    <input
                      type="text"
                      value={formData.address.pincode}
                      onChange={(e) => handleAddressChange('pincode', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('address.pincode') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    />
                    {hasFieldError('address.pincode') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('address.pincode')}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Documents</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Number</label>
                    <input
                      type="text"
                      value={formData.aadharNumber || ''}
                      onChange={(e) => handleFieldChange('aadharNumber', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('aadharNumber') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="12-digit Aadhar number"
                    />
                    {hasFieldError('aadharNumber') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('aadharNumber')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
                    <input
                      type="text"
                      value={formData.panNumber || ''}
                      onChange={(e) => handleFieldChange('panNumber', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                        hasFieldError('panNumber') ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="ABCDE1234F"
                    />
                    {hasFieldError('panNumber') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('panNumber')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Aadhar Card Upload</label>
                    <ImageUpload
                      currentImage={formData.documents.aadharCard}
                      onImageChange={(value) => handleDocumentChange('aadharCard', value)}
                      label="Upload Aadhar"
                      placeholder="Click to upload Aadhar document"
                      uploadType="document"
                    />
                    {hasFieldError('documents.aadharCard') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('documents.aadharCard')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">PAN Card Upload</label>
                    <ImageUpload
                      currentImage={formData.documents.panCard}
                      onImageChange={(value) => handleDocumentChange('panCard', value)}
                      label="Upload PAN"
                      placeholder="Click to upload PAN document"
                      uploadType="document"
                    />
                    {hasFieldError('documents.panCard') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('documents.panCard')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Statement</label>
                    <ImageUpload
                      currentImage={formData.documents.bankStatement}
                      onImageChange={(value) => handleDocumentChange('bankStatement', value)}
                      label="Upload Bank Statement"
                      placeholder="Click to upload bank statement"
                      uploadType="document"
                    />
                    {hasFieldError('documents.bankStatement') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('documents.bankStatement')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Salary Slip</label>
                    <ImageUpload
                      currentImage={formData.documents.salarySlip}
                      onImageChange={(value) => handleDocumentChange('salarySlip', value)}
                      label="Upload Salary Slip"
                      placeholder="Click to upload salary slip"
                      uploadType="document"
                    />
                    {hasFieldError('documents.salarySlip') && (
                      <p className="text-red-500 text-xs mt-1">{getFieldError('documents.salarySlip')}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Status - Only show for admin users */}
              {user?.role === 'admin' && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Status & Remarks</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={formData.status}
                        onChange={(e) => handleFieldChange('status', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                          hasFieldError('status') ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        <option value="pending">Pending</option>
                        <option value="under_review">Under Review</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="disbursed">Disbursed</option>
                      </select>
                      {hasFieldError('status') && (
                        <p className="text-red-500 text-xs mt-1">{getFieldError('status')}</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                      <textarea
                        value={formData.remarks}
                        onChange={(e) => handleFieldChange('remarks', e.target.value)}
                        rows={3}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                          hasFieldError('remarks') ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter any remarks..."
                      />
                      {hasFieldError('remarks') && (
                        <p className="text-red-500 text-xs mt-1">{getFieldError('remarks')}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    isSubmitting 
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                      : 'bg-orange-600 text-white hover:bg-orange-700'
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Saving...</span>
                    </div>
                  ) : (
                    application ? 'Update' : 'Create'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Applications;